// One date, printed into the separate boxes a form splits it across.
//
// CONSUMER: shared (merge-context-builder + node-pool tests).
//
// Nate, 2026-09-25: the AAR purchase contract's close-of-escrow line is
// three blanks, "____ MONTH / ____ DAY, 20__ YEAR", and the OS only had
// the date as one string ("October 24, 2026"). One string cannot fill
// three boxes, so the line went out empty even though the offer had the
// date on it.
//
// Pure and dependency-free. Parsed by hand rather than through `Date`,
// because `new Date("2026-10-24")` is midnight UTC and prints October 23
// in Arizona, which is exactly the one-day-early closing date a contract
// must never carry.
//
// An unreadable value gives NULL, never a guess. The offer's close date
// is whatever a person accepted, and "45 days after acceptance" is a
// real answer that has no month in it. Null leaves the box empty, and
// the send guard then stops the document and names the blank.

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export interface DateParts {
  /** "October" — the month box on the AAR forms wants the word. */
  month: string;
  /** "24" — no leading zero, the way a person writes it. */
  day: string;
  /** "2026". */
  year: string;
  /** "26" — for a box printed after a pre-filled "20". */
  yearShort: string;
}

function monthIndexFromWord(word: string): number | null {
  const w = word.toLowerCase().replace(/\.$/, "");
  if (w.length < 3) return null;
  const i = MONTHS.findIndex((m) => m.toLowerCase().startsWith(w));
  return i >= 0 ? i : null;
}

function build(year: number, monthIndex: number, day: number): DateParts | null {
  if (!Number.isInteger(year) || year < 1900 || year > 2199) return null;
  if (!Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11) return null;
  // Days in the month, leap years included, so "February 30" is refused
  // rather than printed.
  const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  if (!Number.isInteger(day) || day < 1 || day > daysInMonth) return null;
  const yearText = String(year);
  return {
    month: MONTHS[monthIndex] ?? "",
    day: String(day),
    year: yearText,
    yearShort: yearText.slice(-2),
  };
}

/**
 * Split a date as the OS stores or prints it into its parts.
 *
 * Accepts the three shapes a date actually reaches a contract in:
 *   "2026-10-24" (a column, optionally with a time after it)
 *   "October 24, 2026" / "Oct 24 2026" (what an offer holds)
 *   "10/24/2026" (what a person types)
 */
export function datePartsFromText(value: string | null | undefined): DateParts | null {
  const text = (value ?? "").trim();
  if (!text) return null;

  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/.exec(text);
  if (iso) return build(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));

  const slashed = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  if (slashed) return build(Number(slashed[3]), Number(slashed[1]) - 1, Number(slashed[2]));

  const worded = /^([A-Za-z]+\.?)\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/.exec(text);
  if (worded) {
    const m = monthIndexFromWord(worded[1] ?? "");
    if (m === null) return null;
    return build(Number(worded[3]), m, Number(worded[2]));
  }
  return null;
}
